import React, { memo, useCallback, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiSmartphone, FiTablet, FiMonitor, FiCpu, FiBattery, FiWatch, FiHeadphones, FiHardDrive,
  FiCamera, FiPrinter, FiTool, FiSearch, FiChevronLeft, FiChevronRight, FiStar, FiZap,
  FiShield, FiTruck, FiCreditCard, FiRefreshCw, FiAlertCircle, FiBox, FiSpeaker, FiTv, FiMic, FiMapPin,
} from 'react-icons/fi';
import { RiMacbookLine, RiGamepadLine } from 'react-icons/ri';
import { useAppLanguage, languageDirection } from '../../common/utils/useAppLanguage';
import api from '../../common/api/api';
import { resolveImageSrc } from '../../common/utils/productutils';

const EMPTY = Object.freeze([]);
const EMPTY_PRICE = Object.freeze({ cur: '', amount: '' });
const PRODUCTS_URL = '/api/products';
const MAX_PRODUCTS = 30;
const MAX_DEALS = 12;
const MAX_GRID = 12;
const CACHE_TTL = 300000;
const FRESH_MS = 14 * 24 * 60 * 60 * 1000;
const LOCALES = { en: 'en-US', ar: 'ar-EG', fr: 'fr-FR' };
const ACCENT = '#39ff88';

const productRoute = (id) => `/device/${encodeURIComponent(id)}`;
const shopRoute = (id) => `/shops/${encodeURIComponent(id)}`;
const searchRoute = (q) => `/devices?search=${encodeURIComponent(q)}`;
const SHOPS_URL = '/api/users/shops/all';
const SEARCH_MIN = 2;
const SEARCH_MAX = 100;
const SEARCH_PRODUCTS = 5;
const SEARCH_SHOPS = 3;
const SHOPS_FETCH_SIZE = 100;
const categoryRoute = (id) => `/products/category/${encodeURIComponent(id)}`;
const CATEGORIES_URL = '/api/categories';
const MAX_CATEGORIES = 30;

const THEMES = {
  light: {
    '--bg': '#f9fafb', '--card': '#ffffff', '--ink': '#052e1e', '--soft': 'rgba(5,46,30,.68)',
    '--line': '#000000', '--sh': '#000000', '--sk1': '#e5e7eb', '--sk2': '#f3f4f6', '--bar': 'rgba(249,250,251,.92)',
  },
  dark: {
    '--bg': '#141414', '--card': '#1c1c1e', '--ink': '#ffffff', '--soft': 'rgba(255,255,255,.66)',
    '--line': 'rgba(255,255,255,.16)', '--sh': '#0b4a35', '--sk1': '#232326', '--sk2': '#2e2e32', '--bar': 'rgba(20,20,20,.92)',
  },
};

const TEXT = {
  en: {
    searchLabel: 'Search devices, parts and shops',
    searchPlaceholder: 'Search phones, laptops, parts…',
    searchProducts: 'Products',
    searchShops: 'Shops',
    searchAll: 'See all results for “{{q}}”',
    searchNone: 'No matches found',
    searchLoading: 'Searching…',
    categoriesLabel: 'Categories',
    dealsTitle: 'Today’s deals',
    endsIn: 'Ends in',
    newTitle: 'New arrivals',
    viewAll: 'View all',
    fresh: 'New',
    off: (p) => `${p}% off`,
    save: 'Save',
    prev: 'Previous',
    next: 'Next',
    shopsTitle: 'Find a trusted shop',
    shopsSub: 'Compare rated shops near you',
    shopsCta: 'Browse shops',
    repairTitle: 'Fix your device',
    repairSub: 'Book a technician in minutes',
    repairCta: 'Book a repair',
    trackTitle: 'Track your order',
    trackSub: 'See where your package is now',
    trackCta: 'Track order',
    trust: [
      { title: 'Verified shops', sub: 'Every seller is checked' },
      { title: 'Fast delivery', sub: 'Tracked from shop to door' },
      { title: 'Secure payment', sub: 'Your details stay protected' },
      { title: 'Easy returns', sub: 'Warranty on every listing' },
    ],
    errorTitle: 'Products could not be loaded',
    errorSub: 'Check your connection and try again.',
    retry: 'Try again',
    emptyTitle: 'No products yet',
    emptySub: 'New listings will appear here soon.',
  },
  ar: {
    searchLabel: 'ابحث عن أجهزة وقطع غيار ومتاجر',
    searchPlaceholder: 'ابحث عن هواتف، لابتوب، قطع غيار…',
    searchProducts: 'المنتجات',
    searchShops: 'المتاجر',
    searchAll: 'عرض كل النتائج لـ «{{q}}»',
    searchNone: 'لا توجد نتائج مطابقة',
    searchLoading: 'جارٍ البحث…',
    categoriesLabel: 'الأقسام',
    dealsTitle: 'عروض اليوم',
    endsIn: 'ينتهي خلال',
    newTitle: 'وصل حديثًا',
    viewAll: 'عرض الكل',
    fresh: 'جديد',
    off: (p) => `خصم ${p}%`,
    save: 'وفّر',
    prev: 'السابق',
    next: 'التالي',
    shopsTitle: 'اعثر على متجر موثوق',
    shopsSub: 'قارن المتاجر المقيّمة القريبة منك',
    shopsCta: 'تصفّح المتاجر',
    repairTitle: 'أصلح جهازك',
    repairSub: 'احجز فنيًا في دقائق',
    repairCta: 'احجز إصلاحًا',
    trackTitle: 'تتبّع طلبك',
    trackSub: 'اعرف مكان شحنتك الآن',
    trackCta: 'تتبّع الطلب',
    trust: [
      { title: 'متاجر موثّقة', sub: 'نراجع كل بائع' },
      { title: 'توصيل سريع', sub: 'تتبّع من المتجر إلى بابك' },
      { title: 'دفع آمن', sub: 'بياناتك محمية' },
      { title: 'إرجاع سهل', sub: 'ضمان على كل منتج' },
    ],
    errorTitle: 'تعذّر تحميل المنتجات',
    errorSub: 'تحقق من الاتصال وحاول مرة أخرى.',
    retry: 'حاول مرة أخرى',
    emptyTitle: 'لا توجد منتجات بعد',
    emptySub: 'ستظهر المنتجات الجديدة هنا قريبًا.',
  },
  fr: {
    searchLabel: 'Rechercher des appareils, pièces et boutiques',
    searchPlaceholder: 'Téléphones, ordinateurs, pièces…',
    searchProducts: 'Produits',
    searchShops: 'Boutiques',
    searchAll: 'Voir tous les résultats pour « {{q}} »',
    searchNone: 'Aucun résultat',
    searchLoading: 'Recherche…',
    categoriesLabel: 'Catégories',
    dealsTitle: 'Offres du jour',
    endsIn: 'Se termine dans',
    newTitle: 'Nouveautés',
    viewAll: 'Voir tout',
    fresh: 'Nouveau',
    off: (p) => `-${p}%`,
    save: 'Économisez',
    prev: 'Précédent',
    next: 'Suivant',
    shopsTitle: 'Trouvez une boutique de confiance',
    shopsSub: 'Comparez les boutiques notées près de vous',
    shopsCta: 'Voir les boutiques',
    repairTitle: 'Réparez votre appareil',
    repairSub: 'Réservez un technicien en quelques minutes',
    repairCta: 'Réserver une réparation',
    trackTitle: 'Suivez votre commande',
    trackSub: 'Voyez où est votre colis maintenant',
    trackCta: 'Suivre ma commande',
    trust: [
      { title: 'Boutiques vérifiées', sub: 'Chaque vendeur est contrôlé' },
      { title: 'Livraison rapide', sub: 'Suivi de la boutique à la porte' },
      { title: 'Paiement sécurisé', sub: 'Vos données restent protégées' },
      { title: 'Retours faciles', sub: 'Garantie sur chaque annonce' },
    ],
    errorTitle: 'Impossible de charger les produits',
    errorSub: 'Vérifiez votre connexion et réessayez.',
    retry: 'Réessayer',
    emptyTitle: 'Aucun produit pour le moment',
    emptySub: 'Les nouvelles annonces apparaîtront bientôt ici.',
  },
};

const CATEGORY_RULES = [
  [/laptop|notebook|macbook|ordinateur|computer|\bpc\b|لابتوب|حاسوب|كمبيوتر/, RiMacbookLine],
  [/tablet|ipad|tablette|تابلت|لوحي|ايباد/, FiTablet],
  [/watch|wearable|smartband|montre|ساعه|ساعة|سوار/, FiWatch],
  [/speaker|haut-parleur|مكبر|سبيكر/, FiSpeaker],
  [/head|earbud|earphone|airpod|buds|audio|casque|ecouteur|سماع|صوتي/, FiHeadphones],
  [/microphone|\bmic\b|ميكروفون/, FiMic],
  [/camera|photo|lens|drone|appareil photo|كاميرا|تصوير/, FiCamera],
  [/television|\btv\b|smart tv|تلفزيون|تلفاز/, FiTv],
  [/monitor|screen|display|ecran|شاشه|شاشة|شاشات/, FiMonitor],
  [/gam|console|playstation|xbox|nintendo|joystick|controller|jeux|العاب/, RiGamepadLine],
  [/print|scanner|طابع|imprim/, FiPrinter],
  [/storage|drive|\bssd\b|\bhdd\b|\busb\b|flash|memory|disk|stockage|disque|memoire|ذاكره|ذاكرة|تخزين/, FiHardDrive],
  [/charger|cable|power|battery|adapter|chargeur|batterie|شاحن|كابل|بطاريه|بطارية|باور/, FiBattery],
  [/cpu|gpu|processor|motherboard|\bram\b|component|composant|معالج|قطع/, FiCpu],
  [/repair|tool|réparation|صيانة|اصلاح|إصلاح/, FiTool],
  [/accessor|case|cover|housse|coque|اكسسوار|ملحقات|جراب|حافظه|حافظة/, FiBox],
  [/phone|mobile|smartphone|iphone|android|galaxy|telephone|portable|هاتف|جوال|موبايل|محمول|تليفون/, FiSmartphone],
];
const iconCache = new Map();
const categoryIconFor = (name) => {
  const key = String(name ?? '').normalize('NFKD').replace(/[\u0300-\u036f\u064B-\u065F\u0670\u0640]/g, '').toLowerCase();
  if (iconCache.has(key)) return iconCache.get(key);
  const hit = CATEGORY_RULES.find(([re]) => re.test(key));
  const Icon = hit ? hit[1] : FiBox;
  iconCache.set(key, Icon);
  return Icon;
};

const TRUST_ICONS = [FiShield, FiTruck, FiCreditCard, FiRefreshCw];

const STYLES = `
  .mk-root { font-family: 'Poppins', 'Cairo', system-ui, -apple-system, 'Segoe UI', sans-serif; color: var(--ink); }
  .mk-root[dir="rtl"] { font-family: 'Cairo', 'Poppins', system-ui, sans-serif; }
  .mk-pt { padding-top: var(--mk-nav); }
  @media (min-width: 640px) { .mk-pt { padding-top: var(--mk-nav-sm); } }
  .mk-scroll { scrollbar-width: none; overscroll-behavior-x: contain; -webkit-overflow-scrolling: touch; }
  .mk-scroll::-webkit-scrollbar { display: none; }
  .mk-card, .mk-svc { transition: transform .22s cubic-bezier(.16,1,.3,1); }
  .mk-img { transition: transform .5s cubic-bezier(.16,1,.3,1); }
  .mk-ill { transition: transform .35s cubic-bezier(.16,1,.3,1); transform-origin: 50% 60%; }
  @media (hover: hover) {
    .mk-card:hover, .mk-svc:hover { transform: translate3d(0, -3px, 0); }
    .mk-card:hover .mk-img { transform: scale(1.06); }
    .mk-svc:hover .mk-ill { transform: rotate(-3deg) scale(1.05); }
  }
  .mk-card:active, .mk-svc:active { transform: translate3d(0, 2px, 0); }
  .mk-focus:focus-visible { outline: 3px solid ${ACCENT}; outline-offset: 3px; }
  @keyframes mkShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
  .mk-sk { background: linear-gradient(90deg, var(--sk1) 25%, var(--sk2) 50%, var(--sk1) 75%); background-size: 200% 100%; animation: mkShimmer 1.4s linear infinite; }
  @media (prefers-reduced-motion: reduce) {
    .mk-sk { animation: none; }
    .mk-card, .mk-svc, .mk-img, .mk-ill { transition: none !important; }
    .mk-card:hover, .mk-svc:hover, .mk-card:hover .mk-img, .mk-svc:hover .mk-ill { transform: none; }
  }
`;

const FONT_URLS = {
  poppins: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap',
  cairo: 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap',
};
const requestedFonts = new Set();
const addLink = (rel, href, crossOrigin) => {
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (crossOrigin) link.crossOrigin = crossOrigin;
  document.head.appendChild(link);
};
const ensureFont = (key) => {
  if (typeof document === 'undefined' || requestedFonts.has(key)) return;
  if (!requestedFonts.size) {
    addLink('preconnect', 'https://fonts.googleapis.com');
    addLink('preconnect', 'https://fonts.gstatic.com', 'anonymous');
  }
  requestedFonts.add(key);
  addLink('stylesheet', FONT_URLS[key]);
};

let stylesInjected = false;
const injectStylesOnce = () => {
  if (stylesInjected || typeof document === 'undefined') return;
  const tag = document.createElement('style');
  tag.setAttribute('data-market-styles', 'true');
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  stylesInjected = true;
};
injectStylesOnce();

const clean = (v) => (typeof v === 'string' ? v.replace(/[\p{Cc}\p{Cf}]/gu, ' ').trim() : '');
const text = (v) => clean(v).slice(0, 160);
const num = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const ts = (v) => {
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : 0;
};

const SAFE_DATA = /^data:image\/(png|jpe?g|webp|gif|avif);base64,[a-z0-9+/=]+$/i;
const SAFE_RELATIVE = /^(?!\/\/)[\w\-./%~]+$/;
const isSafeUrl = (s) => /^https?:\/\/[^\s]+$/i.test(s) || SAFE_DATA.test(s) || SAFE_RELATIVE.test(s);
const resolveSrc = (v) => {
  try { return resolveImageSrc(v) || v; } catch (e) { return v; }
};
const safeImg = (v) => {
  const s = clean(v);
  if (!s || s.includes('..') || s.length > (s.startsWith('data:') ? 300000 : 2048)) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(s) || s.startsWith('//')) return isSafeUrl(s) ? s : '';
  const resolved = resolveSrc(s);
  return typeof resolved === 'string' && isSafeUrl(resolved) ? resolved : '';
};
const pickImage = (o) => {
  const first = Array.isArray(o.images) ? o.images[0] : undefined;
  return (
    safeImg(o.image) || safeImg(o.imageUrl) || safeImg(o.thumbnail) ||
    safeImg(typeof first === 'string' ? first : first?.url)
  );
};

const toProduct = (p) => {
  if (!p || typeof p !== 'object' || p.deleted) return null;
  const id = p.id ?? p._id;
  const name = text(p.name ?? p.title);
  if (id == null || !name) return null;
  const sale = num(p.finalPrice ?? p.discountPrice ?? p.salePrice ?? p.discountedPrice);
  const list = num(p.originalPrice ?? p.oldPrice ?? p.price);
  const price = sale > 0 && sale < list ? sale : num(p.price) || list;
  const oldPrice = list > price ? list : 0;
  const created = ts(p.createdAt ?? p.created_at ?? p.createdDate);
  const rating = Math.min(5, num(p.rating ?? p.averageRating));
  return {
    id: String(id).slice(0, 120),
    name,
    image: pickImage(p),
    shop: text(p.shopName ?? p.shop?.name),
    price,
    oldPrice,
    saved: oldPrice ? oldPrice - price : 0,
    percent: oldPrice ? Math.round(((oldPrice - price) / oldPrice) * 100) : 0,
    rating,
    created,
    fresh: created > 0 && Date.now() - created < FRESH_MS,
  };
};

const normalizeProducts = (raw) => {
  if (!Array.isArray(raw)) return EMPTY;
  const list = [];
  for (const entry of raw) {
    const item = toProduct(entry);
    if (item) list.push(item);
  }
  if (list.some((v) => v.created)) list.sort((a, b) => b.created - a.created);
  return list.slice(0, MAX_PRODUCTS);
};

const toList = (raw, depth = 0) => {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object' || depth > 3) return EMPTY;
  const inner = raw.data ?? raw.content ?? raw.items ?? raw.results ?? raw.products;
  if (Array.isArray(inner)) return inner;
  return inner && typeof inner === 'object' ? toList(inner, depth + 1) : EMPTY;
};

const normalizeCategories = (raw) => {
  const seen = new Set();
  const out = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object' || c.id == null) continue;
    const id = String(c.id).slice(0, 128);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: text(c.name ?? c.title) || id });
    if (out.length >= MAX_CATEGORIES) break;
  }
  return out;
};

const catCache = { at: 0, promise: null };
const loadCategories = () => {
  if (catCache.promise && Date.now() - catCache.at < CACHE_TTL) return catCache.promise;
  const promise = api
    .get(CATEGORIES_URL, { timeout: 15000 })
    .then((res) => normalizeCategories(toList(res.data)))
    .catch(() => {
      catCache.promise = null;
      return EMPTY;
    });
  catCache.at = Date.now();
  catCache.promise = promise;
  return promise;
};

const useCategories = () => {
  const [state, setState] = useState({ loading: true, list: EMPTY });
  useEffect(() => {
    let alive = true;
    loadCategories().then((list) => { if (alive) setState({ loading: false, list }); });
    return () => { alive = false; };
  }, []);
  return state;
};

const fold = (v) =>
  String(v ?? '').normalize('NFKD').replace(/[\u0300-\u036f\u064B-\u065F\u0670\u0640]/g, '').toLowerCase();

const toSearchProduct = (p) => {
  const base = toProduct(p);
  if (!base) return null;
  const category = text(p.categoryName ?? (typeof p.category === 'string' ? p.category : ''));
  return { ...base, _n: fold(base.name), _k: fold(`${base.name} ${text(p.brand)} ${category} ${text(p.description)}`) };
};

const toSearchShop = (s) => {
  if (!s || typeof s !== 'object' || s.id == null) return null;
  const name = text(s.name);
  if (!name) return null;
  const address = [s.shopAddress?.street, s.shopAddress?.city].map(text).filter(Boolean).join(', ');
  return { id: String(s.id).slice(0, 128), name, address, _n: fold(name), _k: fold(`${name} ${address} ${text(s.description)}`) };
};

const rankMatches = (items, term, limit) => {
  const tokens = term.split(/\s+/).filter(Boolean);
  if (!tokens.length) return EMPTY;
  const scored = [];
  for (const it of items) {
    if (!tokens.every((tk) => it._k.includes(tk))) continue;
    scored.push([it._n.startsWith(term) ? 0 : it._n.includes(term) ? 1 : 2, it]);
  }
  scored.sort((a, b) => a[0] - b[0]);
  return scored.slice(0, limit).map((x) => x[1]);
};

const searchCache = { at: 0, promise: null };
const loadSearchData = () => {
  if (searchCache.promise && Date.now() - searchCache.at < CACHE_TTL) return searchCache.promise;
  const promise = Promise.allSettled([
    api.get(PRODUCTS_URL, { timeout: 15000 }),
    api.get(SHOPS_URL, { params: { page: 0, size: SHOPS_FETCH_SIZE }, timeout: 15000 }),
  ]).then(([p, sh]) => {
    if (p.status === 'rejected' && sh.status === 'rejected') searchCache.promise = null;
    return {
      products: p.status === 'fulfilled' ? toList(p.value.data).map(toSearchProduct).filter(Boolean) : EMPTY,
      shops: sh.status === 'fulfilled' ? toList(sh.value.data).map(toSearchShop).filter(Boolean) : EMPTY,
    };
  });
  searchCache.at = Date.now();
  searchCache.promise = promise;
  return promise;
};

const useSearchData = (enabled) => {
  const [state, setState] = useState({ loading: false, products: EMPTY, shops: EMPTY });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    loadSearchData().then((d) => { if (alive) setState({ loading: false, ...d }); });
    return () => { alive = false; };
  }, [enabled]);
  return state;
};

const EMPTY_MATCH = Object.freeze({ products: EMPTY, shops: EMPTY });

const cache = { at: 0, promise: null };
const loadProducts = (force) => {
  if (!force && cache.promise && Date.now() - cache.at < CACHE_TTL) return cache.promise;
  const promise = api
    .get(PRODUCTS_URL, { timeout: 15000 })
    .then((res) => normalizeProducts(toList(res.data)))
    .catch((err) => {
      cache.promise = null;
      throw err;
    });
  cache.at = Date.now();
  cache.promise = promise;
  return promise;
};

const useProducts = (source) => {
  const hasSource = Array.isArray(source);
  const [state, setState] = useState({ status: hasSource ? 'ready' : 'loading', list: EMPTY });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (hasSource) return undefined;
    let alive = true;
    setState((s) => (s.status === 'loading' ? s : { status: 'loading', list: EMPTY }));
    loadProducts(attempt > 0)
      .then((list) => { if (alive) setState({ status: 'ready', list }); })
      .catch(() => { if (alive) setState({ status: 'error', list: EMPTY }); });
    return () => { alive = false; };
  }, [hasSource, attempt]);

  const sourceItems = useMemo(() => (hasSource ? normalizeProducts(source) : EMPTY), [hasSource, source]);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  if (hasSource) return { products: sourceItems, status: 'ready', retry };
  return { products: state.list, status: state.status, retry };
};

const useMotion = (rootRef, ready) => {
  useEffect(() => {
    if (!ready || typeof window === 'undefined') return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let disposed = false;
    let cleanup = () => {};

    Promise.all([import('gsap'), import('gsap/ScrollTrigger'), import('lenis')])
      .then(([gsapModule, stModule, lenisModule]) => {
        const root = rootRef.current;
        if (disposed || !root) return;
        const gsap = gsapModule.gsap || gsapModule.default;
        const ScrollTrigger = stModule.ScrollTrigger || stModule.default;
        const Lenis = lenisModule.default || lenisModule.Lenis;
        gsap.registerPlugin(ScrollTrigger);

        const lenis = new Lenis({ duration: 1.1, smoothWheel: true, syncTouch: false });
        lenis.on('scroll', ScrollTrigger.update);
        const tick = (time) => lenis.raf(time * 1000);
        gsap.ticker.add(tick);
        gsap.ticker.lagSmoothing(0);

        const ctx = gsap.context(() => {
          gsap.utils.toArray('[data-reveal]').forEach((el) => {
            gsap.from(el, {
              autoAlpha: 0, y: 18, duration: 0.6, ease: 'power3.out', clearProps: 'all',
              scrollTrigger: { trigger: el, start: 'top 90%', once: true },
            });
          });
          gsap.utils.toArray('[data-stagger]').forEach((el) => {
            const items = Array.from(el.children);
            if (!items.length) return;
            gsap.from(items, {
              autoAlpha: 0, y: 22, duration: 0.5, ease: 'power3.out', stagger: 0.05, clearProps: 'all',
              scrollTrigger: { trigger: el, start: 'top 88%', once: true },
            });
          });
        }, root);

        ScrollTrigger.refresh();

        cleanup = () => {
          ctx.revert();
          gsap.ticker.remove(tick);
          lenis.destroy();
        };
      })
      .catch(() => {});

    return () => {
      disposed = true;
      cleanup();
    };
  }, [rootRef, ready]);
};

const formatters = {};
const getFormatter = (locale) => {
  if (formatters[locale]) return formatters[locale];
  let parts = (v) => ({ cur: '', amount: String(v) });
  try {
    const m = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EGP',
      currencyDisplay: locale.startsWith('ar') ? 'symbol' : 'code',
      maximumFractionDigits: 0,
    });
    parts = (v) => {
      let cur = '';
      let amount = '';
      for (const p of m.formatToParts(v)) {
        if (p.type === 'currency') cur += p.value;
        else if (p.type !== 'literal') amount += p.value;
      }
      return { cur, amount };
    };
  } catch (e) {
    parts = (v) => ({ cur: '', amount: String(v) });
  }
  formatters[locale] = parts;
  return parts;
};

const hideBroken = (e) => { e.currentTarget.style.visibility = 'hidden'; };
const pad = (n) => String(n).padStart(2, '0');
const msToMidnight = () => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(24, 0, 0, 0);
  return Math.max(0, end.getTime() - now.getTime());
};

const Arrow = memo(({ className = 'h-4 w-4' }) => (
  <svg className={`${className} shrink-0 rtl:rotate-180`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
));

const Placeholder = memo(() => (
  <svg className="mx-auto h-full w-1/2 text-emerald-700 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="2.5" width="14" height="19" rx="3" />
    <path d="M10.5 18.5h3" />
  </svg>
));

const Countdown = memo(({ label }) => {
  const [left, setLeft] = useState(msToMidnight);
  useEffect(() => {
    const id = setInterval(() => setLeft(msToMidnight()), 1000);
    return () => clearInterval(id);
  }, []);
  const total = Math.floor(left / 1000);
  const units = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[var(--soft)] sm:text-xs">
      <span>{label}</span>
      <span className="flex items-center gap-1" dir="ltr">
        {units.map((u, i) => (
          <span key={i} className="rounded-md bg-black px-1.5 py-0.5 font-bold tabular-nums text-[#39ff88]">{pad(u)}</span>
        ))}
      </span>
    </div>
  );
});

const SectionHead = memo(({ title, to, cta, icon: Icon, children }) => (
  <div data-reveal className="mb-3 flex items-end justify-between gap-3 sm:mb-4">
    <div className="flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
      <h2 className="flex items-center gap-2 text-lg font-extrabold leading-tight sm:text-2xl">
        {Icon && (
          <span className="grid h-7 w-7 flex-none place-items-center rounded-lg border-2 border-black bg-[#39ff88] text-emerald-950 sm:h-9 sm:w-9 sm:border-[3px]">
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          </span>
        )}
        <span className="truncate">{title}</span>
      </h2>
      {children}
    </div>
    {to && (
      <Link to={to} className="mk-focus inline-flex flex-none items-center gap-1 rounded-lg px-1 py-1 text-xs font-bold text-emerald-600 hover:underline sm:text-sm">
        {cta}
        <Arrow className="h-3.5 w-3.5" />
      </Link>
    )}
  </div>
));

const ProductCard = memo(({ item, eager, t, parts, rail }) => {
  const { cur, amount } = item.price > 0 ? parts(item.price) : EMPTY_PRICE;
  const savedText = item.saved > 0 ? parts(item.saved).amount : '';
  const badge = item.percent > 0 ? t.off(item.percent) : item.fresh ? t.fresh : '';
  const sizing = rail ? 'w-[8.75rem] flex-none snap-start sm:w-48 lg:w-52' : 'w-full';
  return (
    <Link
      to={productRoute(item.id)}
      draggable={false}
      className={`mk-card mk-focus group relative flex flex-col overflow-hidden rounded-xl border-2 border-[var(--line)] bg-[var(--card)] p-1.5 outline-none [box-shadow:calc(var(--dx)*3px)_3px_0_0_var(--sh)] sm:rounded-2xl sm:border-[3px] sm:p-2.5 sm:[box-shadow:calc(var(--dx)*5px)_5px_0_0_var(--sh)] ${sizing}`}
    >
      <div className="relative aspect-square overflow-hidden rounded-lg bg-white p-1.5 sm:rounded-xl sm:p-3">
        {badge && (
          <span className={`absolute start-1 top-1 z-10 rounded-md px-1.5 py-0.5 text-[10px] font-bold leading-none sm:start-2 sm:top-2 sm:text-[11px] ${item.percent > 0 ? 'bg-[#39ff88] text-emerald-950' : 'bg-amber-400 text-amber-950'}`}>
            {badge}
          </span>
        )}
        {item.image ? (
          <img
            src={item.image}
            alt=""
            width={320}
            height={320}
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
            decoding="async"
            draggable={false}
            referrerPolicy="no-referrer"
            onError={hideBroken}
            className="mk-img h-full w-full object-contain"
          />
        ) : (
          <Placeholder />
        )}
      </div>
      <div className="mt-2 flex min-w-0 flex-1 flex-col gap-0.5 sm:mt-3 sm:gap-1">
        <h3 className="line-clamp-2 min-h-[2.1rem] text-xs font-semibold leading-snug text-[var(--ink)] sm:min-h-[2.6rem] sm:text-sm">{item.name}</h3>
        {item.shop && <p className="truncate text-[10px] font-medium text-[var(--soft)] sm:text-xs">{item.shop}</p>}
        {item.rating > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-amber-500 sm:text-xs">
            <FiStar className="h-3 w-3 fill-current" aria-hidden="true" />
            {item.rating.toFixed(1)}
          </span>
        )}
        {item.price > 0 && (
          <p className="mt-auto flex flex-wrap items-baseline gap-x-1 gap-y-0 pt-0.5">
            {cur && <span className="text-[10px] font-bold text-[var(--soft)] sm:text-xs">{cur}</span>}
            <span className="text-sm font-extrabold tabular-nums sm:text-lg">{amount}</span>
            {item.oldPrice > 0 && <span className="ms-0.5 text-[10px] tabular-nums text-[var(--soft)] line-through sm:text-xs">{parts(item.oldPrice).amount}</span>}
            {savedText && <span className="ms-0.5 hidden rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600 sm:inline">{t.save} {savedText}</span>}
          </p>
        )}
      </div>
    </Link>
  );
});

const CardSkeleton = memo(({ rail }) => (
  <div
    aria-hidden="true"
    className={`overflow-hidden rounded-xl border-2 border-[var(--line)] bg-[var(--card)] p-1.5 sm:rounded-2xl sm:border-[3px] sm:p-2.5 ${rail ? 'w-[8.75rem] flex-none sm:w-48 lg:w-52' : 'w-full'}`}
  >
    <div className="mk-sk aspect-square rounded-lg sm:rounded-xl" />
    <div className="mk-sk mt-2 h-3 w-11/12 rounded sm:mt-3" />
    <div className="mk-sk mt-1.5 h-3 w-2/3 rounded" />
    <div className="mk-sk mt-2 h-4 w-1/2 rounded" />
  </div>
));

const GridSkeleton = memo(({ count = 8 }) => (
  <div aria-hidden="true" className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-6">
    {Array.from({ length: count }, (_, i) => <CardSkeleton key={i} />)}
  </div>
));

const RailSkeleton = memo(() => (
  <div aria-hidden="true" className="flex gap-2.5 overflow-hidden pb-2 sm:gap-4">
    {Array.from({ length: 6 }, (_, i) => <CardSkeleton key={i} rail />)}
  </div>
));

const Rail = memo(({ children, prevLabel, nextLabel, isAr }) => {
  const ref = useRef(null);
  const scrollBy = useCallback((dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (isAr ? -1 : 1) * el.clientWidth * 0.8, behavior: 'smooth' });
  }, [isAr]);
  const btn = 'mk-focus absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer place-items-center rounded-full border-[3px] border-black bg-[#39ff88] text-emerald-950 [box-shadow:2px_2px_0_0_#000] transition-transform duration-150 active:scale-95 sm:grid';
  return (
    <div className="relative">
      <div ref={ref} data-stagger className="mk-scroll flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-0.5 pb-3 pt-0.5 sm:gap-4">
        {children}
      </div>
      <button type="button" aria-label={prevLabel} onClick={() => scrollBy(-1)} className={`${btn} start-1`}>
        <FiChevronLeft className="h-5 w-5 rtl:rotate-180" aria-hidden="true" />
      </button>
      <button type="button" aria-label={nextLabel} onClick={() => scrollBy(1)} className={`${btn} end-1`}>
        <FiChevronRight className="h-5 w-5 rtl:rotate-180" aria-hidden="true" />
      </button>
    </div>
  );
});

const StatePanel = memo(({ icon: Icon, title, sub, action, onAction }) => (
  <div className="flex flex-col items-center gap-2 rounded-2xl border-[3px] border-dashed border-[var(--line)] bg-[var(--card)] px-4 py-10 text-center">
    <Icon className="h-8 w-8 text-emerald-500" aria-hidden="true" />
    <p className="text-base font-bold sm:text-lg">{title}</p>
    <p className="text-xs text-[var(--soft)] sm:text-sm">{sub}</p>
    {action && (
      <button
        type="button"
        onClick={onAction}
        className="mk-focus mt-2 inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border-[3px] border-black bg-[#39ff88] px-5 py-2 text-sm font-bold text-emerald-950 [box-shadow:calc(var(--dx)*4px)_4px_0_0_#000] transition-transform duration-150 active:translate-y-1"
      >
        <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
        {action}
      </button>
    )}
  </div>
));

const SearchThumb = memo(({ option }) => {
  if (option.kind === 'product') {
    return (
      <span className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-lg border border-black/10 bg-white">
        {option.item.image ? (
          <img
            src={option.item.image}
            alt=""
            width={40}
            height={40}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={hideBroken}
            className="h-full w-full object-contain p-1"
          />
        ) : (
          <FiSmartphone className="h-4 w-4 text-gray-400" aria-hidden="true" />
        )}
      </span>
    );
  }
  return (
    <span className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-[#39ff88] text-emerald-950">
      {option.kind === 'shop' ? <FiMapPin className="h-4 w-4" aria-hidden="true" /> : <FiSearch className="h-4 w-4" aria-hidden="true" />}
    </span>
  );
});

const SearchRow = memo(({ id, index, option, active, label, parts, onPick, onHover }) => {
  const pick = useCallback(() => onPick(option), [onPick, option]);
  const hover = useCallback(() => onHover(index), [onHover, index]);
  const isAll = option.kind === 'all';
  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={pick}
      onMouseEnter={hover}
      className={`flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2 ${active ? 'bg-emerald-500/15' : ''} ${isAll ? 'border-t-2 border-[var(--line)]' : ''}`}
    >
      <SearchThumb option={option} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm ${isAll ? 'font-bold text-emerald-600' : 'font-semibold'}`}>
          {isAll ? label : option.item.name}
        </span>
        {option.kind === 'product' && option.item.price > 0 && (
          <span dir="ltr" className="block truncate text-xs font-semibold tabular-nums text-[var(--soft)]">
            {(() => { const p = parts(option.item.price); return `${p.cur} ${p.amount}`.trim(); })()}
          </span>
        )}
        {option.kind === 'shop' && option.item.address && (
          <span className="block truncate text-xs font-medium text-[var(--soft)]">{option.item.address}</span>
        )}
      </span>
    </li>
  );
});

const SearchBox = memo(({ t, parts }) => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const listId = useId();
  const [value, setValue] = useState('');
  const [warm, setWarm] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const deferred = useDeferredValue(value);
  const term = useMemo(() => fold(deferred).trim(), [deferred]);
  const hasTerm = term.length >= SEARCH_MIN;
  const { products, shops, loading } = useSearchData(warm);

  const matches = useMemo(
    () => (hasTerm ? { products: rankMatches(products, term, SEARCH_PRODUCTS), shops: rankMatches(shops, term, SEARCH_SHOPS) } : EMPTY_MATCH),
    [hasTerm, products, shops, term]
  );
  const found = matches.products.length + matches.shops.length;
  const trimmed = value.replace(/[\p{Cc}\p{Cf}]/gu, ' ').trim();

  const options = useMemo(() => {
    if (!hasTerm) return EMPTY;
    const out = [];
    for (const p of matches.products) out.push({ key: `p${p.id}`, kind: 'product', item: p, to: productRoute(p.id) });
    for (const s of matches.shops) out.push({ key: `s${s.id}`, kind: 'shop', item: s, to: shopRoute(s.id) });
    out.push({ key: 'all', kind: 'all', to: searchRoute(trimmed) });
    return out;
  }, [hasTerm, matches, trimmed]);

  useEffect(() => { setActive(-1); }, [term]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onFocus = useCallback(() => { setWarm(true); setOpen(true); }, []);
  const onChange = useCallback((e) => { setValue(e.target.value.slice(0, SEARCH_MAX)); setOpen(true); }, []);
  const onPick = useCallback((option) => { setOpen(false); navigate(option.to); }, [navigate]);
  const onHover = useCallback((i) => setActive(i), []);

  const onKeyDown = useCallback((e) => {
    if (!options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a <= 0 ? options.length - 1 : a - 1));
    }
  }, [options.length]);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    if (!trimmed) return;
    const chosen = open && active >= 0 ? options[active] : null;
    setOpen(false);
    navigate(chosen ? chosen.to : searchRoute(trimmed));
  }, [trimmed, open, active, options, navigate]);

  const showList = open && hasTerm;
  const allLabel = t.searchAll.split('{{q}}').join(trimmed);
  let lastKind = null;

  return (
    <form ref={wrapRef} role="search" onSubmit={onSubmit} className="relative">
      <label htmlFor={`${listId}-input`} className="sr-only">{t.searchLabel}</label>
      <FiSearch className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--soft)] sm:h-5 sm:w-5" aria-hidden="true" />
      <input
        id={`${listId}-input`}
        type="search"
        role="combobox"
        value={value}
        maxLength={SEARCH_MAX}
        autoComplete="off"
        enterKeyHint="search"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        onFocus={onFocus}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={t.searchPlaceholder}
        className="h-11 w-full rounded-xl border-[3px] border-black bg-[var(--card)] ps-9 pe-3 text-sm font-medium text-[var(--ink)] outline-none placeholder:text-[var(--soft)] focus-visible:border-[#39ff88] sm:h-12 sm:ps-11 sm:text-base"
      />
      {showList && (
        <div className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border-[3px] border-[var(--line)] bg-[var(--card)] text-[var(--ink)] [box-shadow:calc(var(--dx)*4px)_4px_0_0_var(--sh)]">
          <ul id={listId} role="listbox" aria-label={t.searchLabel} className="mk-scroll max-h-[380px] overflow-y-auto py-1">
            {found === 0 && (
              <li role="presentation" className="px-4 py-3 text-sm font-semibold text-[var(--soft)]">
                {loading ? t.searchLoading : t.searchNone}
              </li>
            )}
            {options.map((o, i) => {
              const header = o.kind !== 'all' && o.kind !== lastKind ? (
                <li role="presentation" className="px-4 pb-1 pt-2 text-[11px] font-bold text-[var(--soft)]">
                  {o.kind === 'product' ? t.searchProducts : t.searchShops}
                </li>
              ) : null;
              lastKind = o.kind;
              return (
                <React.Fragment key={o.key}>
                  {header}
                  <SearchRow id={`${listId}-${i}`} index={i} option={o} active={i === active} label={allLabel} parts={parts} onPick={onPick} onHover={onHover} />
                </React.Fragment>
              );
            })}
          </ul>
        </div>
      )}
    </form>
  );
});

const CategoryBar = memo(({ t, parts, categories, loading }) => {
  return (
    <div
      className="relative z-30 border-b-2 border-[var(--line)]"
      style={{ backgroundColor: 'var(--bar)' }}
    >
      <div className="mx-auto w-full max-w-[120rem] px-3 py-2.5 sm:px-5 sm:py-3">
        <SearchBox t={t} parts={parts} />
        {(loading || categories.length > 0) && (
          <nav aria-label={t.categoriesLabel} className="mk-scroll -mx-3 mt-2.5 flex gap-2 overflow-x-auto px-3 pb-0.5 sm:mx-0 sm:px-0">
            {loading
              ? Array.from({ length: 8 }, (_, i) => (
                <span key={i} aria-hidden="true" className="mk-sk h-9 w-24 flex-none rounded-full" />
              ))
              : categories.map((c) => {
                const Icon = categoryIconFor(c.name);
                return (
                  <Link
                    key={c.id}
                    to={categoryRoute(c.id)}
                    className="mk-focus inline-flex min-h-9 flex-none items-center gap-1.5 rounded-full border-2 border-[var(--line)] bg-[var(--card)] px-3 py-1.5 text-xs font-semibold transition-colors duration-150 hover:bg-[#39ff88] hover:text-emerald-950 sm:text-sm"
                  >
                    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
                    {c.name}
                  </Link>
                );
              })}
          </nav>
        )}
      </div>
    </div>
  );
});

const ShopIllustration = memo(({ className }) => (
  <svg className={`mk-ill pointer-events-none flex-none ${className}`} viewBox="0 0 160 140" fill="none" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" focusable="false">
    <rect x="28" y="56" width="104" height="70" rx="6" fill="#fff" stroke="#000" strokeWidth="3" />
    <rect x="52" y="6" width="56" height="18" rx="5" fill="#39ff88" stroke="#000" strokeWidth="3" />
    <path d="M62 15h36" stroke="#000" strokeWidth="3" />
    <rect x="22" y="30" width="29" height="26" fill="#fbbf24" stroke="#000" strokeWidth="3" />
    <rect x="51" y="30" width="29" height="26" fill="#fff" stroke="#000" strokeWidth="3" />
    <rect x="80" y="30" width="29" height="26" fill="#fbbf24" stroke="#000" strokeWidth="3" />
    <rect x="109" y="30" width="29" height="26" fill="#fff" stroke="#000" strokeWidth="3" />
    <rect x="36" y="70" width="18" height="24" rx="3" fill="#99f6e4" stroke="#000" strokeWidth="3" />
    <rect x="106" y="70" width="18" height="24" rx="3" fill="#99f6e4" stroke="#000" strokeWidth="3" />
    <rect x="62" y="84" width="36" height="42" rx="4" fill="#0b4a35" stroke="#000" strokeWidth="3" />
    <circle cx="90" cy="106" r="2.5" fill="#39ff88" />
    <path d="M140 10l3.5 8 8.5.5-6.5 5.5 2 9-7.5-5-7.5 5 2-9-6.5-5.5 8.5-.5z" fill="#fff" stroke="#000" strokeWidth="3" />
  </svg>
));

const RepairIllustration = memo(({ className }) => (
  <svg className={`mk-ill pointer-events-none flex-none ${className}`} viewBox="0 0 160 140" fill="none" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="30" cy="40" r="19" stroke="#39ff88" strokeWidth="5" strokeDasharray="6 5.2" />
    <circle cx="30" cy="40" r="13" fill="#39ff88" stroke="#000" strokeWidth="3" />
    <circle cx="30" cy="40" r="4.5" fill="#052e1e" />
    <rect x="46" y="14" width="60" height="108" rx="10" fill="#fff" stroke="#000" strokeWidth="3" />
    <rect x="53" y="28" width="46" height="78" rx="4" fill="#0b4a35" />
    <rect x="68" y="19" width="16" height="3" rx="1.5" fill="#000" />
    <path d="M62 30l11 26-9 14 20 30" stroke="#39ff88" strokeWidth="3" />
    <path d="M120 112l16-50" stroke="#000" strokeWidth="15" />
    <path d="M120 112l16-50" stroke="#fbbf24" strokeWidth="9" />
    <circle cx="138" cy="56" r="12" fill="#fbbf24" stroke="#000" strokeWidth="3" />
    <circle cx="138" cy="52" r="4.5" fill="#052e1e" />
    <path d="M22 104v14M15 111h14M128 14v10M123 19h10" stroke="#39ff88" strokeWidth="3" />
  </svg>
));

const TrackIllustration = memo(({ className }) => (
  <svg className={`mk-ill pointer-events-none flex-none ${className}`} viewBox="0 0 160 140" fill="none" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" focusable="false">
    <rect x="14" y="28" width="132" height="98" rx="10" fill="#fff" stroke="#000" strokeWidth="3" />
    <path d="M14 60h132M14 92h132M56 28v98M102 28v98" stroke="#e5e7eb" strokeWidth="3" />
    <path d="M30 108C46 58 84 126 124 68" stroke="#0b4a35" strokeWidth="4" strokeDasharray="1 9" />
    <circle cx="30" cy="108" r="7" fill="#fbbf24" stroke="#000" strokeWidth="3" />
    <rect x="62" y="74" width="32" height="28" rx="3" fill="#fbbf24" stroke="#000" strokeWidth="3" />
    <path d="M78 74v28M62 86h32" stroke="#000" strokeWidth="3" />
    <path d="M126 10a16 16 0 0 1 16 16c0 14-16 40-16 40S110 40 110 26a16 16 0 0 1 16-16z" fill="#10b981" stroke="#000" strokeWidth="3" />
    <circle cx="126" cy="26" r="5.5" fill="#fff" stroke="#000" strokeWidth="3" />
  </svg>
));

const dots = (base, dot) => ({
  backgroundColor: base,
  backgroundImage: `radial-gradient(${dot} 2px, transparent 2.5px)`,
  backgroundSize: '18px 18px',
});
const grid = (base, line) => ({
  backgroundColor: base,
  backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
  backgroundSize: '22px 22px',
});
const stripes = (base, line) => ({
  backgroundColor: base,
  backgroundImage: `repeating-linear-gradient(135deg, ${line} 0 2px, transparent 2px 14px)`,
});

const SERVICES = [
  {
    key: 'shops', to: '/shops', Art: ShopIllustration, title: 'shopsTitle', sub: 'shopsSub', cta: 'shopsCta',
    light: {
      style: dots('#d1fae5', 'rgba(5,150,105,.28)'), border: '#000', shadow: '#000',
      ink: 'text-emerald-950', soft: 'text-emerald-900/80', chip: 'bg-emerald-950 text-white',
    },
    dark: {
      style: dots('#0f2a20', 'rgba(57,255,136,.16)'), border: 'rgba(57,255,136,.45)', shadow: '#0b4a35',
      ink: 'text-white', soft: 'text-white/70', chip: 'bg-[#39ff88] text-emerald-950',
    },
  },
  {
    key: 'repair', to: '/repair', Art: RepairIllustration, title: 'repairTitle', sub: 'repairSub', cta: 'repairCta',
    light: {
      style: grid('#ccfbf1', 'rgba(13,148,136,.2)'), border: '#000', shadow: '#000',
      ink: 'text-teal-950', soft: 'text-teal-900/80', chip: 'bg-teal-900 text-white',
    },
    dark: {
      style: grid('#0a1f18', 'rgba(57,255,136,.12)'), border: 'rgba(57,255,136,.45)', shadow: '#0b4a35',
      ink: 'text-white', soft: 'text-white/70', chip: 'bg-[#39ff88] text-emerald-950',
    },
  },
  {
    key: 'track', to: '/track', Art: TrackIllustration, title: 'trackTitle', sub: 'trackSub', cta: 'trackCta',
    light: {
      style: stripes('#ecfccb', 'rgba(77,124,15,.16)'), border: '#000', shadow: '#000',
      ink: 'text-lime-950', soft: 'text-lime-900/80', chip: 'bg-lime-900 text-white',
    },
    dark: {
      style: stripes('#1a2410', 'rgba(190,242,100,.12)'), border: 'rgba(190,242,100,.42)', shadow: '#3f5212',
      ink: 'text-white', soft: 'text-white/70', chip: 'bg-lime-300 text-lime-950',
    },
  },
];

const ServiceCard = memo(({ svc, t, darkMode }) => {
  const { Art } = svc;
  const v = darkMode ? svc.dark : svc.light;
  return (
    <Link
      to={svc.to}
      draggable={false}
      style={{ ...v.style, borderColor: v.border, '--svc-sh': v.shadow }}
      className="mk-svc mk-focus group relative flex min-h-[5.5rem] w-full flex-row items-center justify-between gap-2 overflow-hidden rounded-2xl border-[3px] p-3 outline-none [box-shadow:calc(var(--dx)*4px)_4px_0_0_var(--svc-sh)] sm:min-h-[13rem] sm:flex-col sm:items-start sm:rounded-3xl sm:p-5 sm:[box-shadow:calc(var(--dx)*6px)_6px_0_0_var(--svc-sh)]"
    >
      <div className="relative z-10 flex min-w-0 flex-col gap-0.5 sm:gap-1">
        <h3 className={`text-base font-extrabold leading-tight sm:text-xl ${v.ink}`}>{t[svc.title]}</h3>
        <p className={`text-[11px] font-medium leading-snug sm:text-sm ${v.soft}`}>{t[svc.sub]}</p>
        <span className={`mt-1.5 inline-flex w-fit items-center gap-1.5 rounded-lg border-2 border-black px-2.5 py-1 text-xs font-bold sm:mt-2 sm:rounded-xl sm:border-[3px] sm:px-4 sm:py-2 sm:text-sm ${v.chip}`}>
          {t[svc.cta]}
          <Arrow className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
        </span>
      </div>
      <Art className="h-16 w-16 drop-shadow-[0_6px_10px_rgba(0,0,0,.25)] sm:h-28 sm:w-28 sm:self-end lg:h-32 lg:w-32" />
    </Link>
  );
});

const TrustBar = memo(({ t }) => (
  <ul data-stagger className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
    {t.trust.map((item, i) => {
      const Icon = TRUST_ICONS[i];
      return (
        <li key={item.title} className="flex items-center gap-2.5 rounded-xl border-2 border-[var(--line)] bg-[var(--card)] p-2.5 sm:gap-3 sm:rounded-2xl sm:border-[3px] sm:p-4">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-lg border-2 border-black bg-[#39ff88] text-emerald-950 sm:h-11 sm:w-11 sm:rounded-xl sm:border-[3px]">
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-xs font-bold sm:text-sm">{item.title}</p>
            <p className="line-clamp-2 text-[10px] leading-snug text-[var(--soft)] sm:text-xs">{item.sub}</p>
          </div>
        </li>
      );
    })}
  </ul>
));

const MarketPage = ({ darkMode = false, products: source, navOffset = 150, navOffsetSm = 132 }) => {
  const language = useAppLanguage();
  const isAr = language === 'ar';
  const dir = languageDirection(language);
  const t = TEXT[language] ?? TEXT.en;
  const parts = getFormatter(LOCALES[language] ?? LOCALES.en);
  const rootRef = useRef(null);
  const { products, status, retry } = useProducts(source);
  const { list: categories, loading: categoriesLoading } = useCategories();
  const loading = status === 'loading';

  useEffect(() => {
    ensureFont('poppins');
    if (isAr) ensureFont('cairo');
  }, [isAr]);

  useMotion(rootRef, !loading);

  const deals = useMemo(
    () => products.filter((p) => p.percent > 0).sort((a, b) => b.percent - a.percent).slice(0, MAX_DEALS),
    [products]
  );
  const arrivals = useMemo(() => products.slice(0, MAX_GRID), [products]);

  const rootStyle = useMemo(
    () => ({
      ...(darkMode ? THEMES.dark : THEMES.light),
      backgroundColor: 'var(--bg)',
      '--mk-nav': `${navOffset}px`,
      '--mk-nav-sm': `${navOffsetSm}px`,
    }),
    [darkMode, navOffset, navOffsetSm]
  );

  return (
    <div
      ref={rootRef}
      dir={dir}
      className="mk-root mk-pt relative isolate min-h-screen [--dx:1] rtl:[--dx:-1]"
      style={rootStyle}
      aria-busy={loading}
    >
      <CategoryBar t={t} parts={parts} categories={categories} loading={categoriesLoading} />

      <main className="mx-auto flex w-full max-w-[120rem] flex-col gap-7 px-3 pb-12 pt-8 sm:gap-12 sm:px-5 sm:pb-16 sm:pt-12">
        {status === 'error' && (
          <StatePanel icon={FiAlertCircle} title={t.errorTitle} sub={t.errorSub} action={t.retry} onAction={retry} />
        )}

        {(loading || deals.length > 0) && (
          <section aria-label={t.dealsTitle}>
            <SectionHead title={t.dealsTitle} to="/devices?sort=discount" cta={t.viewAll} icon={FiZap}>
              <Countdown label={t.endsIn} />
            </SectionHead>
            {loading ? (
              <RailSkeleton />
            ) : (
              <Rail prevLabel={t.prev} nextLabel={t.next} isAr={isAr}>
                {deals.map((item, i) => (
                  <ProductCard key={item.id} item={item} eager={i < 2} t={t} parts={parts} rail />
                ))}
              </Rail>
            )}
          </section>
        )}

        <section aria-label={t.shopsTitle}>
          <div data-stagger className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-5">
            {SERVICES.map((svc) => (
              <ServiceCard key={svc.key} svc={svc} t={t} darkMode={darkMode} />
            ))}
          </div>
        </section>

        <section aria-label={t.newTitle}>
          <SectionHead title={t.newTitle} to="/devices" cta={t.viewAll} />
          {loading && <GridSkeleton />}
          {!loading && status === 'ready' && arrivals.length === 0 && (
            <StatePanel icon={FiSmartphone} title={t.emptyTitle} sub={t.emptySub} />
          )}
          {!loading && arrivals.length > 0 && (
            <div data-stagger className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-6">
              {arrivals.map((item, i) => (
                <ProductCard key={item.id} item={item} eager={i < 4 && deals.length === 0} t={t} parts={parts} />
              ))}
            </div>
          )}
        </section>

        <TrustBar t={t} />
      </main>
    </div>
  );
};

export default memo(MarketPage);